/**
 * InlineEditText — click-to-edit title text, no modal.
 *
 * Shows text + pencil icon on hover; click transforms into an input inline.
 * Enter or blur saves; Escape cancels. Spinner while saving.
 */
import { useRef, useState } from "react";
import { CircleNotch, PencilSimple } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

export interface InlineEditTextProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}

export function InlineEditText({
  value,
  onSave,
  placeholder = "Sem nome",
  className,
  inputClassName,
  disabled,
}: InlineEditTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const cancelledRef = useRef(false);

  function startEdit(e: React.MouseEvent) {
    if (disabled || editing) return;
    e.stopPropagation();
    cancelledRef.current = false;
    setDraft(value);
    setEditing(true);
  }

  async function save() {
    if (saving) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
    } catch {
      // caller surfaces error via toast
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  function cancel() {
    cancelledRef.current = true;
    setDraft(value);
    setEditing(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    e.stopPropagation();
    if (e.key === "Enter") { e.preventDefault(); void save(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  }

  function handleBlur() {
    if (cancelledRef.current) return;
    void save();
  }

  if (editing) {
    return (
      <span
        className="inline-flex items-center min-w-0 w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={saving}
          placeholder={placeholder}
          className={cn(
            "bg-muted/40 rounded px-2 py-0.5 border border-primary/50 outline-none w-full min-w-0",
            "text-foreground placeholder:text-muted-foreground/40",
            "focus:border-primary focus:bg-muted/60 transition-colors",
            inputClassName,
          )}
          style={{ fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit" }}
        />
        {saving && (
          <CircleNotch
            weight="bold"
            className="ml-2 w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0"
          />
        )}
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") startEdit(e as unknown as React.MouseEvent);
      }}
      className={cn(
        "group inline-flex items-center gap-1.5 min-w-0 rounded px-1.5 py-0.5 -mx-1.5",
        !disabled && [
          "cursor-pointer",
          "hover:bg-muted/50 transition-colors duration-100",
        ],
        className,
      )}
      title={disabled ? undefined : "Clique para renomear"}
    >
      <span className="truncate">
        {value || <span className="text-muted-foreground/50 italic">{placeholder}</span>}
      </span>
      {!disabled && (
        <PencilSimple
          weight="regular"
          aria-hidden
          className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-100"
        />
      )}
    </span>
  );
}
